/**
 * @file Customers.jsx
 * @description Page de gestion des clients de l'application
 * 
 * Cette page gère l'affichage et la manipulation des clients avec les fonctionnalités suivantes :
 * 
 * 1. Gestion des données
 *    - Chargement initial automatique via useEffect
 *    - Rechargement automatique après les opérations CRUD
 *    - Gestion des états de chargement et d'erreur
 *    - Pagination et filtrage côté client
 * 
 * 2. Configuration du tableau
 *    - Colonnes personnalisables avec tri
 *    - Filtres primaires et secondaires
 *    - Actions sur les lignes et en-tête
 *    - Support multilingue
 * 
 * 3. Gestion des états
 *    - État de chargement (loading)
 *    - Gestion des erreurs
 *    - Pagination (page courante, items par page)
 *    - Filtres et recherche
 *    - Configuration du tri
 * 
 * 4. Fonctionnalités de rafraîchissement
 *    - Rechargement manuel via refreshData
 *    - Rechargement automatique après CRUD
 *    - Réinitialisation de la pagination lors des changements
 * 
 * 5. Internationalisation
 *    - Support multilingue (FR, ES, AR)
 *    - Traductions des libellés
 *    - Format des nombres et dates
 * 
 * @example
 * // Utilisation du composant
 * <Customers />
 * 
 * @example
 * // Configuration du tableau
 * const tableConfig = {
 *   pagination: { ... },
 *   sorting: { ... }
 * };
 * 
 * @example
 * // Configuration des colonnes
 * const columns = [
 *   {
 *     key: 'name',
 *     label: 'Nom',
 *     sortable: true,
 *     visible: true
 *   }
 * ];
 * 
 * @example
 * // Configuration des filtres
 * const filterConfig = {
 *   primary: [
 *     {
 *       type: 'select',
 *       key: 'category',
 *       label: 'Catégorie',
 *       options: [...]
 *     }
 *   ]
 * };
 * 
 * @example
 * // Gestion du rechargement des données
 * const refreshData = useCallback(() => {
 *   setRefreshTrigger(prev => prev + 1);
 * }, []);
 * 
 * @example
 * // Chargement automatique des données
 * useEffect(() => {
 *   fetchData({
 *     page: currentPage,
 *     limit: itemsPerPage,
 *     search,
 *     filters,
 *     sortConfig
 *   });
 * }, [fetchData, currentPage, itemsPerPage, search, filters, sortConfig, refreshTrigger]);
 */

import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useLanguage } from '../../../../../hooks/useLanguage';
import { customers as frCustomers } from '../../../../../locales/fr/customers';
import { customers as esCustomers } from '../../../../../locales/es/customers';
import { customers as arCustomers } from '../../../../../locales/ar/customers';
import { CustomerService } from '../../../../../services/AppDataServices/services/customer.service';
import DataTable from '../../../components/DataTable';
import FilterSection from '../../../components/DataTable/FilterSection';
import useSearch from '../../../hooks/useSearch';
import PrintableView from '../../../components/DataTable/PrintableView';
import Modal from '../../../components/common/Modal';
import { aside as frAside } from '../../../../../locales/fr/aside';
import { aside as arAside } from '../../../../../locales/ar/aside';
import { aside as esAside } from '../../../../../locales/es/aside';
import useMyAppStore from '../../../../../stores/myAppStore';
import CustomerForm from './CustomerForm';
import FloatingAlert from '../../../../../components/Common/FloatingAlert';
import { formatDate, formatCurrency } from '../../../../../utils';

const translations = {
  fr: frCustomers,
  es: esCustomers,
  ar: arCustomers
};

const asideTranslations = {
  fr: frAside,
  ar: arAside,
  es: esAside
};

const Customers = () => {
  const { lang } = useLanguage();
  const t = translations[lang];
  const asideT = asideTranslations[lang];
  const { setPageTitle } = useMyAppStore();
  const customerService = new CustomerService();
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [alert, setAlert] = useState({ open: false, message: '', type: 'info' });

  // État pour la pagination et les filtres
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({
    key: 'name',
    direction: 'asc'
  });

  // Configuration de la recherche
  const searchConfig = useMemo(() => ({
    minMatchLength: 2,
    fields: [
      { key: 'name', type: 'text' },
      { key: 'phone', type: 'text' },
      { key: 'address', type: 'text' }
    ]
  }), []);

  const { searchItems } = useSearch(searchConfig);

  // Handlers pour les filtres
  const handleSearchChange = useCallback((value) => {
    setSearch(value);
    setCurrentPage(1);
  }, []);

  // Configuration des filtres primaires
  const primaryFilters = useMemo(() => [
    {
      type: 'search',
      key: 'search',
      label: t.filters.search.label || 'Rechercher',
      placeholder: t.filters.search.placeholder || 'Rechercher un client...',
      value: search,
      onChange: handleSearchChange
    },
    {
      type: 'sort',
      key: 'sort',
      label: t.filters.sort?.label || 'Trier par',
      value: sortConfig.key,
      direction: sortConfig.direction,
      onChange: (value) => {
        setSortConfig(prev => ({
          key: value,
          direction: prev.key === value && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
      },
      options: [
        { value: 'name', label: t.columns.name || 'Nom' },
        { value: 'phone', label: t.columns.phone || 'Téléphone' },
        { value: 'solde', label: t.columns.solde || 'Solde' },
        { value: 'createdAt', label: t.columns.createdAt || 'Date de création' }
      ]
    }
  ], [t, search, handleSearchChange, sortConfig]);

  // Configuration des colonnes
  const columns = useMemo(() => [
    {
      key: 'name',
      label: t.columns.name || 'Nom',
      sortable: true,
      visible: true
    },
    {
      key: 'phone',
      label: t.columns.phone || 'Téléphone',
      sortable: true,
      visible: true
    },
    {
      key: 'solde',
      label: t.columns.solde || 'Solde',
      sortable: true,
      visible: true,
      render: (item) => formatCurrency(item.solde || 0)
    },
    {
      key: 'isActive',
      label: t.columns.isActive || 'Statut',
      sortable: true,
      visible: true,
      render: (item) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          item.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {item.isActive ? (t.filters.status?.active || 'Actif') : (t.filters.status?.inactive || 'Inactif')}
        </span>
      )
    }
  ], [t]);

  // Configuration des colonnes collapsibles
  const collapsibleColumns = useMemo(() => [
    {
      key: 'basicInfo',
      label: t.columns.basicInfo || 'Informations de base',
      render: (item) => (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <dt className="min-w-[120px] font-medium">{t.columns.name || 'Nom'}:</dt>
            <dd className="flex-1">{item.name}</dd>
          </div>
          <div className="flex items-center gap-2">
            <dt className="min-w-[120px] font-medium">{t.columns.phone || 'Téléphone'}:</dt>
            <dd className="flex-1">{item.phone}</dd>
          </div>
          <div className="flex items-center gap-2">
            <dt className="min-w-[120px] font-medium">{t.columns.address || 'Adresse'}:</dt>
            <dd className="flex-1">{item.address}</dd>
          </div>
          <div className="flex items-center gap-2">
            <dt className="min-w-[120px] font-medium">{t.columns.notes || 'Notes'}:</dt>
            <dd className="flex-1">{item.notes}</dd>
          </div>
        </div>
      ),
    },
    {
      key: 'financialInfo',
      label: t.columns.financialInfo || 'Détails financiers',
      render: (item) => (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <dt className="min-w-[120px] font-medium">{t.columns.solde || 'Solde'}:</dt>
            <dd className="flex-1 font-semibold">
              {formatCurrency(item.solde || 0)}
            </dd>
          </div>
        </div>
      ),
    },
    {
      key: 'systemInfo',
      label: t.columns.systemInfo || 'Informations système',
      render: (item) => (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <dt className="min-w-[120px] font-medium">{t.columns.createdAt || 'Créé le'}:</dt>
            <dd className="flex-1">{formatDate(item.createdAt)}</dd>
          </div>
          <div className="flex items-center gap-2">
            <dt className="min-w-[120px] font-medium">{t.columns.updatedAt || 'Mis à jour le'}:</dt>
            <dd className="flex-1">{formatDate(item.updatedAt)}</dd>
          </div>
          <div className="flex items-center gap-2">
            <dt className="min-w-[120px] font-medium">{t.columns.lastSync || 'Dernière synchronisation'}:</dt>
            <dd className="flex-1">{item.lastSync ? formatDate(item.lastSync) : 'Non synchronisé'}</dd>
          </div>
        </div>
      ),
    },
  ], [t]);

  // Fonction de récupération des données
  const fetchData = useCallback(async ({ page, limit, search, filters, sortConfig }) => {
    try {
      setLoading(true);
      setError(null);
      
      let customers = await customerService.getAll();
      let total = customers.length;

      // Application de la recherche avancée
      if (search) {
        customers = searchItems(search, customers);
      }

      // Appliquer le tri
      if (sortConfig.key) {
        customers.sort((a, b) => {
          const aValue = a[sortConfig.key];
          const bValue = b[sortConfig.key];
          const direction = sortConfig.direction === 'asc' ? 1 : -1;
          
          if (typeof aValue === 'string') {
            return direction * aValue.localeCompare(bValue);
          }
          return direction * (aValue - bValue);
        });
      }

      // Mettre à jour le total après tous les filtres
      total = customers.length;

      // Pagination
      const start = (page - 1) * limit;
      const end = start + limit;
      customers = customers.slice(start, end);

      setData({ items: customers, total });
    } catch (error) {
      console.error('Error fetching customers:', error);
      setError(error);
    } finally {
      setLoading(false);
    }
  }, [searchItems]);

  // Effet pour charger les données
  useEffect(() => {
    fetchData({
      page: currentPage,
      limit: itemsPerPage,
      search,
      filters,
      sortConfig
    });
  }, [fetchData, currentPage, itemsPerPage, search, filters, sortConfig, refreshTrigger]);

  // Handlers pour les changements d'état
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((limit) => {
    setItemsPerPage(limit);
    setCurrentPage(1);
  }, []);

  const handleSortChange = useCallback((newSortConfig) => {
    setSortConfig(newSortConfig);
  }, []);

  // Fonction pour forcer le rechargement des données
  const refreshData = useCallback(() => {
    // console.log('refreshData');
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // Fonction pour afficher les alertes
  const showAlert = useCallback((message, type = 'info') => {
    setAlert({ open: true, message, type });
  }, []);

  // Fonction pour fermer les alertes
  const handleCloseAlert = useCallback(() => {
    setAlert(prev => ({ ...prev, open: false }));
  }, []);

  // Mise à jour du handler de suppression
  const handleDeleteCustomer = useCallback(async (item) => {
    if (window.confirm(t.confirmations.delete)) {
      try {
        await customerService.deleteCustomer(item.id);
        showAlert(t.confirmations.deleteSuccess, 'success');
        refreshData();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        showAlert(error.message || t.errors.delete, 'error');
      }
    }
  }, [t, refreshData, showAlert]);

  // Configuration des actions
  const actionConfig = useMemo(() => ({
    primary: [
      {
        label: t.actions.newCustomer,
        onClick: () => {
          setSelectedCustomer(null);
          setIsEditMode(false);
          setIsModalOpen(true);
        },
        primary: true
      }
    ],
    row: [
      {
        icon: 'edit',
        onClick: (item) => {
          setSelectedCustomer(item);
          setIsEditMode(true);
          setIsModalOpen(true);
        },
        title: t.actions.edit,
        className: 'text-blue-500 hover:text-blue-700',
        show: (item) => customerService.isCustomerEditable(item)
      },
      {
        icon: 'delete',
        onClick: handleDeleteCustomer,
        title: t.actions.delete,
        className: 'text-red-500 hover:text-red-700',
        show: (item) => customerService.isCustomerEditable(item)
      }
    ]
  }), [t, handleDeleteCustomer]);

  // Configuration complète pour le DataTable
  const dataTableConfig = useMemo(() => ({
    tableConfig: {
      pagination: {
        pageSize: 10,
        pageSizeOptions: [10, 25, 50, 100]
      },
      sorting: {
        defaultSort: { key: 'name', direction: 'asc' }
      }
    },
    columns,
    filters: {
      primary: primaryFilters
    },
    actions: actionConfig,
    data,
    loading,
    error,
    currentPage,
    itemsPerPage,
    totalItems: data.total,
    onPageChange: handlePageChange,
    onItemsPerPageChange: handleItemsPerPageChange,
    onSearch: handleSearchChange,
    onSortChange: handleSortChange,
    onFilterChange: (newFilters) => {
      setFilters(prev => ({ ...prev, ...newFilters }));
      setCurrentPage(1);
    },
    refreshData,
    collapsibleColumns
  }), [
    columns,
    primaryFilters,
    actionConfig,
    data,
    loading,
    error,
    currentPage,
    itemsPerPage,
    handlePageChange,
    handleItemsPerPageChange,
    handleSearchChange,
    handleSortChange,
    refreshData,
    collapsibleColumns
  ]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handlers pour les opérations CRUD
  const handleSubmitCustomer = async (formData) => {
    try {
      setIsSubmitting(true);
      if (isEditMode) {
        await customerService.updateCustomer(selectedCustomer.id, formData);
        showAlert(t.confirmations.updateSuccess, 'success');
      } else {
        // Créer le client et récupérer le client créé
        const createdCustomer = await customerService.createCustomer(formData);
        showAlert(t.confirmations.createSuccess, 'success');
        
        // Passer en mode édition avec le client créé
        setSelectedCustomer(createdCustomer);
        setIsEditMode(true); // Passer en mode édition
      }
      refreshData();
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
      showAlert(error.message || t.errors.update, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fonction pour remettre en mode création
  const handleResetToCreate = () => {
    setSelectedCustomer(null);
    setIsEditMode(false); // Retour en mode création
  };

  useEffect(() => {
    setPageTitle(asideT.routes.customers || 'Clients');
  }, [lang, asideT.routes.customers, setPageTitle]);

  return (
    <>
      <div className="space-y-4 print-hide">
        <FilterSection
          primaryFilters={primaryFilters}
          onSearch={handleSearchChange}
          onFilterChange={(newFilters) => {
            setFilters(prev => ({ ...prev, ...newFilters }));
            setCurrentPage(1);
          }}
          onSortChange={handleSortChange}
          onAdd={() => {
            setSelectedCustomer(null);
            setIsEditMode(false);
            setIsModalOpen(true);
          }}
        />
        <DataTable {...dataTableConfig} />
      </div>
      <PrintableView
        title={t.title || 'Clients'}
        columns={columns}
        data={data.items}
        translations={t}
      />
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={isEditMode ? t.form.editTitle : t.form.createTitle}
        width="max-w-xl"
      >
        <CustomerForm
          customer={selectedCustomer}
          isEditMode={isEditMode}
          onSubmit={handleSubmitCustomer}
          onCancel={() => setIsModalOpen(false)}
          isSubmitting={isSubmitting}
          alert={alert}
          onAlertClose={handleCloseAlert}
          onResetToCreate={handleResetToCreate}
        />
      </Modal>
      <FloatingAlert
        open={alert.open}
        onClose={handleCloseAlert}
        type={alert.type}
      >
        {alert.message}
      </FloatingAlert>
    </>
  );
};

export default Customers; 